import { create } from 'zustand'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'

const activeSorted = (rows) =>
    (rows || []).filter((s) => s.is_active).sort((a, b) => a.order - b.order)

export const useCrmSettingsStore = create((set, get) => ({
    settings: null,
    sources: [],
    accountUsers: [],
    stages: [],
    loading: false,

    fetchAll: async () => {
        set({ loading: true })
        try {
            const [settingsRes, stagesRes] = await Promise.all([
                fetcher.getCrmSettings(),
                fetcher.getCrmStages(),
            ])
            const s = settingsRes?.data || {}
            set({
                settings: s,
                sources: s.sources || [],
                accountUsers: s.account_users || [],
                stages: activeSorted(stagesRes?.data),
                loading: false,
            })
        } catch (e) {
            set({ loading: false })
            toast.error('Could not load settings.')
        }
    },

    // Optimistic org-level setting update.
    updateSetting: async (patch) => {
        const prev = get().settings
        set({ settings: { ...prev, ...patch } })
        try {
            const res = await fetcher.updateCrmSettings(patch)
            set({ settings: { ...get().settings, ...res.data } })
        } catch (e) {
            set({ settings: prev })
            toast.error('Could not save that setting.')
        }
    },

    runRenewals: async () => {
        try {
            const res = await fetcher.runCrmRenewals()
            const n = res?.data?.created ?? 0
            toast.success(n ? `Surfaced ${n} renewal${n === 1 ? '' : 's'} onto the pipeline.` : 'No leases due for renewal right now.')
        } catch (e) {
            toast.error('Could not run the renewal engine.')
        }
    },

    _reloadStages: async () => {
        const res = await fetcher.getCrmStages()
        set({ stages: activeSorted(res?.data) })
    },

    addStage: async (name) => {
        if (!name.trim()) return
        try {
            await fetcher.createCrmStage({ name: name.trim(), order: get().stages.length })
            await get()._reloadStages()
        } catch (e) {
            toast.error(e?.response?.data?.name || 'Could not add stage.')
        }
    },

    renameStage: async (id, name) => {
        try {
            await fetcher.updateCrmStage(id, { name })
            await get()._reloadStages()
        } catch (e) {
            toast.error(e?.response?.data?.name || 'Could not rename stage.')
        }
    },

    deleteStage: async (id) => {
        try {
            await fetcher.deleteCrmStage(id)
            await get()._reloadStages()
        } catch (e) {
            toast.error('Could not remove stage.')
        }
    },

    moveStage: async (id, dir) => {
        const stages = [...get().stages]
        const i = stages.findIndex((s) => s.id === id)
        const j = i + dir
        if (i < 0 || j < 0 || j >= stages.length) return
        ;[stages[i], stages[j]] = [stages[j], stages[i]]
        set({ stages }) // optimistic
        try {
            await fetcher.reorderCrmStages(stages.map((s) => s.id))
        } catch (e) {
            await get()._reloadStages()
            toast.error('Could not reorder stages.')
        }
    },
}))
